import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { EventBus } from './game/EventBus';
import type { GameSnapshot } from './game/types';
import { createRecordRepository, type RunRecord } from './records';

const PhaserGame = lazy(() =>
  import('./game/PhaserGame').then((module) => ({ default: module.PhaserGame })),
);

type View = 'home' | 'game' | 'records';

const INITIAL_SNAPSHOT: GameSnapshot = {
  phase: 'idle',
  level: 1,
  score: 0,
  lives: 3,
  ballCount: 0,
  greenRemaining: 0,
  runSeed: '',
  muted: false,
};

function readMutedPreference(): boolean {
  try {
    return localStorage.getItem('neon-breaker.muted') === 'true';
  } catch {
    return false;
  }
}

function formatDuration(durationMs: number): string {
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export default function App(): React.JSX.Element {
  const repository = useMemo(() => createRecordRepository(), []);
  const [view, setView] = useState<View>('home');
  const [engineReady, setEngineReady] = useState(false);
  const [snapshot, setSnapshot] = useState<GameSnapshot>(INITIAL_SNAPSHOT);
  const [records, setRecords] = useState<RunRecord[]>([]);
  const [bestRecord, setBestRecord] = useState<RunRecord | null>(null);
  const [muted, setMuted] = useState(readMutedPreference);
  const [compatError, setCompatError] = useState('');

  const refreshRecords = useCallback(async () => {
    const [recent, best] = await Promise.all([
      repository.listRuns(20),
      repository.getBestRecord(),
    ]);
    setRecords(recent);
    setBestRecord(best);
  }, [repository]);

  useEffect(() => {
    queueMicrotask(() => void refreshRecords());
    const unlisten = [
      EventBus.on('game:ready', () => {
        setEngineReady(true);
        EventBus.emit('command:mute', { muted });
      }),
      EventBus.on('game:snapshot', setSnapshot),
      EventBus.on('game:compat-error', setCompatError),
      EventBus.on('game:run-ended', (record) => {
        void repository.saveRun(record).then(refreshRecords);
        if (record.result === 'abandoned') setView('home');
      }),
    ];
    return () => unlisten.forEach((stop) => stop());
  }, [muted, refreshRecords, repository]);

  const startGame = (): void => {
    setCompatError('');
    setView('game');
    EventBus.emit('command:start', {});
  };

  const toggleMuted = (): void => {
    const nextMuted = !muted;
    setMuted(nextMuted);
    try {
      localStorage.setItem('neon-breaker.muted', String(nextMuted));
    } catch {
      // Preference storage is optional.
    }
    EventBus.emit('command:mute', { muted: nextMuted });
  };

  const showHome = (): void => {
    if (snapshot.phase !== 'idle' && snapshot.phase !== 'game-over') {
      EventBus.emit('command:abandon', undefined);
    } else {
      setView('home');
    }
  };

  const isActivePhase = snapshot.phase === 'ready' || snapshot.phase === 'playing';

  return (
    <main className="app-shell" data-testid="app-shell">
      <div className="ambient ambient-one" />
      <div className="ambient ambient-two" />

      <section className="game-frame" aria-label="霓虹破块">
        <Suspense fallback={<div className="canvas-loader" aria-hidden />}>
          <PhaserGame />
        </Suspense>

        <header className="topbar">
          <button
            className="icon-button"
            type="button"
            onClick={toggleMuted}
            aria-label={muted ? '开启声音' : '关闭声音'}
          >
            {muted ? '静' : '声'}
          </button>
          <div className="mini-brand" aria-hidden={view === 'home'}>
            <span className="brand-mark small"><i /><i /><i /></span>
            <span>霓虹破块</span>
          </div>
          {view === 'game' && isActivePhase ? (
            <button
              className="icon-button"
              type="button"
              onClick={() => EventBus.emit('command:pause', undefined)}
              aria-label="暂停游戏"
            >
              Ⅱ
            </button>
          ) : (
            <span className="topbar-spacer" />
          )}
        </header>

        {view === 'game' && (
          <div className="hud" aria-live="polite">
            <div className="hud-item"><span>关卡</span><strong>{snapshot.level}</strong></div>
            <div className="hud-item score"><span>得分</span><strong data-testid="game-score">{snapshot.score.toLocaleString()}</strong></div>
            <div className="hud-item"><span>生命</span><strong data-testid="game-lives">{'◆'.repeat(snapshot.lives) || '—'}</strong></div>
          </div>
        )}

        {view === 'home' && (
          <section className="screen home-screen" data-testid="home-screen">
            <div className="hero-copy">
              <div className="eyebrow">PROCEDURAL ARCADE</div>
              <div className="brand-lockup">
                <span className="brand-mark"><i /><i /><i /></span>
                <h1>霓虹破块</h1>
              </div>
              <p>击碎随机像素阵列，接住增殖核心，让白球铺满整片霓虹。</p>
            </div>

            <div className="power-preview" aria-label="道具说明">
              <div className="power-chip blue"><span className="dot-icon"><i /><i /><i /></span><b>横条发射</b><em>×3</em></div>
              <div className="power-chip yellow"><span className="dot-icon"><i /><i /><i /></span><b>全体分裂</b><em>×3</em></div>
            </div>

            <div className="home-actions">
              <button
                className="primary-button"
                type="button"
                onClick={startGame}
                disabled={!engineReady}
                data-testid="start-game"
              >
                <span>{engineReady ? '开始闯关' : '正在点亮霓虹…'}</span>
                <span aria-hidden>↗</span>
              </button>
              <button className="secondary-button" type="button" onClick={() => setView('records')}>
                本地记录
                <span>{bestRecord ? `最高 ${bestRecord.highestLevel} 关` : '等待首局'}</span>
              </button>
            </div>

            <div className="home-meta">
              <span>拖动横条</span><i />
              <span>点击发射</span><i />
              <span>三次机会</span>
            </div>
          </section>
        )}

        {view === 'records' && (
          <section className="screen records-screen" data-testid="records-screen">
            <div className="section-heading">
              <div>
                <span className="eyebrow">LOCAL RUNS</span>
                <h2>本地记录</h2>
              </div>
              <button className="close-button" type="button" onClick={() => setView('home')} aria-label="返回首页">×</button>
            </div>

            {bestRecord ? (
              <article className="best-card">
                <span>个人最佳</span>
                <strong>{bestRecord.score.toLocaleString()}</strong>
                <div><b>第 {bestRecord.highestLevel} 关</b><em>{formatDuration(bestRecord.durationMs)}</em></div>
              </article>
            ) : (
              <div className="empty-records"><span className="brand-mark"><i /><i /><i /></span><p>还没有闯关记录</p></div>
            )}

            <div className="record-list">
              {records.map((record) => (
                <article className="record-row" key={record.id}>
                  <div><strong>{record.score.toLocaleString()}</strong><span>{formatDate(record.endedAt)}</span></div>
                  <div><b>第 {record.highestLevel} 关</b><span>{record.result === 'game-over' ? '生命耗尽' : '中途退出'}</span></div>
                  <div><b>{record.peakBallCount} 球</b><span>{formatDuration(record.durationMs)}</span></div>
                </article>
              ))}
            </div>
          </section>
        )}

        {view === 'game' && (
          <>
            <div className="game-stats-bottom">
              <span><b>{snapshot.ballCount}</b> BALLS</span>
              <span><b>{snapshot.greenRemaining}</b> BLOCKS</span>
            </div>

            {snapshot.phase === 'ready' && (
              <div className="launch-prompt" data-testid="launch-prompt">
                <span>拖动横条调整位置</span>
                <button type="button" onClick={() => EventBus.emit('command:launch', undefined)}>点击发射</button>
              </div>
            )}

            {snapshot.phase === 'paused' && (
              <section className="modal-backdrop" data-testid="pause-overlay">
                <div className="modal-card">
                  <span className="eyebrow">PAUSED</span>
                  <h2>霓虹暂停</h2>
                  <p>所有轨迹已冻结，回来时将从原位置继续。</p>
                  <button className="primary-button compact" type="button" onClick={() => EventBus.emit('command:resume', undefined)}>继续游戏</button>
                  <button className="text-button" type="button" onClick={showHome}>保存并返回首页</button>
                </div>
              </section>
            )}

            {(snapshot.phase === 'life-lost' || snapshot.phase === 'level-clear') && (
              <div className={`status-banner ${snapshot.phase}`}>
                <span>{snapshot.phase === 'level-clear' ? '关卡清空' : '损失一次机会'}</span>
                <strong>{snapshot.phase === 'level-clear' ? `进入第 ${snapshot.level} 关` : `剩余 ${snapshot.lives} 条生命`}</strong>
              </div>
            )}

            {snapshot.phase === 'game-over' && (
              <section className="modal-backdrop" data-testid="game-over-overlay">
                <div className="modal-card result-card">
                  <span className="eyebrow">RUN COMPLETE</span>
                  <h2>本局结束</h2>
                  <div className="result-score"><span>最终得分</span><strong>{snapshot.score.toLocaleString()}</strong></div>
                  <div className="result-grid"><span>最高关卡<b>{snapshot.level}</b></span><span>峰值球数<b>已记录</b></span></div>
                  <button className="primary-button compact" type="button" onClick={startGame}>再来一局</button>
                  <button className="text-button" type="button" onClick={showHome}>返回首页</button>
                </div>
              </section>
            )}
          </>
        )}

        {compatError && (
          <div className="compat-error" role="alert">
            <span>{compatError}</span>
            <button type="button" onClick={() => setCompatError('')}>知道了</button>
          </div>
        )}
      </section>

      <p className="desktop-hint">鼠标拖动或使用 A / D、方向键控制横条</p>
    </main>
  );
}
